import { useQuery } from "~hooks/useQuery";
import {
  EventErrorType,
  GetEventAndReleaseForCustomerDocument,
} from "~graphql/typed-document-nodes";
import { showToast } from "~lib";
import { useRouter } from "next/router";
import { ToastType } from "react-toastify";
import * as actions from "~features/nonseated-reservation/store/actions";
import { useSDK } from "./useSDK";
import {
  EventPermissions,
  GetEventAndReleaseForCustomerQueryVariables,
} from "~graphql/sdk";

type QueryParams = {
  eventId?: string;
  release?: string;
  slug?: string;
  code?: string;
};

export const useGetEventWithAccessControl = () => {
  const router = useRouter();
  const sdk = useSDK();

  const query = router.query as QueryParams;

  const eventId = query.eventId;
  const releaseId = query.release;
  const accessCodeFromSearchParams = query.code
    ? decodeURIComponent(query.code)
    : undefined;

  const { isLoading, data, error, isValidating } = useQuery(
    eventId ? GetEventAndReleaseForCustomerDocument : null,
    {
      input: {
        eventId,
        releaseId,
        releaseSlug: query.slug,
        code: accessCodeFromSearchParams,
      },
    },
    {
      fetcher: async (input: GetEventAndReleaseForCustomerQueryVariables) => {
        const res = await sdk.getEventAndReleaseForCustomer(input);
        const {
          release,
          redirect,
          errorMessage,
          errorType,
        } = res.getEventAndReleaseForCustomer;

        // Currently redirects are feature flagged on the Backend
        if (redirect) {
          const { slug, code, eventId } = redirect;

          const query = {
            ...router.query,
            eventId,
            ...(slug && { slug }),
            ...(code && { code: encodeURIComponent(code) }),
          };

          return await router.replace({
            pathname: router.pathname,
            query,
          });
        }

        const isTicketsNotOnSaleError =
          errorType === EventErrorType.TicketsNotOnSale;
        const isEventOrSharePage =
          router.pathname === "/events/[eventId]" ||
          router.pathname === "/events/[eventId]/share";
        const shouldSuppressToast =
          isTicketsNotOnSaleError && isEventOrSharePage;

        if (!shouldSuppressToast && errorMessage) {
          showToast(errorMessage, ToastType.ERROR);
        }

        if (accessCodeFromSearchParams) {
          const passwordLookup = await sdk.validateReleasePassword({
            id: release.id,
            password: accessCodeFromSearchParams,
          });
          if (passwordLookup?.validateReleasePassword?.id) {
            sessionStorage.setItem(
              "releasePasswordId",
              passwordLookup.validateReleasePassword?.id
            );
          }
        }

        return res;
      },
      onSuccess: (data) => {
        const { event, release } = data.getEventAndReleaseForCustomer;
        actions.updateTrackingContext({
          id: event.id,
          name: event.title,
          releaseId: release?.id,
          releaseName: release?.name,
        });
      },
    }
  );

  const { event, release, isAdmin, permissions } =
    data?.getEventAndReleaseForCustomer ?? {};

  return {
    isLoading,
    release,
    isAdmin,
    event,
    error,
    isValidating,
    permissions,
    access: {
      infoPage:
        !!permissions &&
        (permissions.includes(EventPermissions.ViewInfo) ||
          permissions.includes(EventPermissions.ViewInfoAdmin)),
      ticketPage:
        !!permissions &&
        (permissions.includes(EventPermissions.ViewTickets) ||
          permissions.includes(EventPermissions.ViewTicketsAdmin)),
    },
  };
};
